import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert,
  Tabs, Tab, Paper, TablePagination, Stepper, Step, StepLabel
} from '@mui/material';
import {
  Add, SwapHoriz, TrendingUp, TrendingDown, Cancel, CheckCircle, LocalShipping, Visibility
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../services/apiClient';
import { LoggerService } from '../services/LoggerService';

// --- Interfaces ---

interface Transaction {
  id: string;
  reference: string;
  type: 'subscription' | 'redemption' | 'switch_out' | 'switch_in';
  investor_id: string;
  investor_name: string;
  share_class_id: string;
  share_class_name: string;
  amount: number | null;
  units: number | null;
  nav_per_share: number | null;
  currency: string;
  status: 'draft' | 'submitted' | 'validated' | 'confirmed' | 'settling' | 'settled' | 'cancelled' | 'rejected';
  order_date: string;
  settlement_date: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface SettlementPipelineItem {
  status: string;
  count: number;
  total_amount: number;
  currency: string;
}

interface ShareClass {
  id: string;
  name: string;
  fund_id: string;
  fund_name: string;
  currency: string;
}

interface PaginatedTransactions {
  items: Transaction[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// --- Constants ---

const TYPE_CHIP_COLOR: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
  subscription: 'success',
  redemption: 'error',
  switch_out: 'warning',
  switch_in: 'info',
};

const STATUS_CHIP_PROPS: Record<string, { color: 'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'; variant?: 'outlined' | 'filled' }> = {
  draft: { color: 'default' },
  submitted: { color: 'info' },
  validated: { color: 'primary' },
  confirmed: { color: 'success' },
  settling: { color: 'warning' },
  settled: { color: 'success', variant: 'outlined' },
  cancelled: { color: 'error' },
  rejected: { color: 'error' },
};

const STATUS_STEPS = ['submitted', 'validated', 'confirmed', 'settling', 'settled'];

const PIPELINE_ORDER = ['submitted', 'validated', 'confirmed', 'settling', 'settled'];

// --- Component ---

const TransactionsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Transaction list state
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');

  // Settlement pipeline
  const [pipeline, setPipeline] = useState<SettlementPipelineItem[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  // Share classes for create dialogs
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);

  // Dialog states
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [createRedOpen, setCreateRedOpen] = useState(false);
  const [createSwitchOpen, setCreateSwitchOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);

  // Selected transaction for dialogs
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);

  // Form state — subscription
  const [subForm, setSubForm] = useState({ investor_id: '', share_class_id: '', amount: '', currency: '' });
  // Form state — redemption
  const [redForm, setRedForm] = useState({ investor_id: '', share_class_id: '', units: '' });
  // Form state — switch
  const [switchForm, setSwitchForm] = useState({ investor_id: '', from_share_class_id: '', to_share_class_id: '', units: '' });
  // Form state — confirm
  const [confirmNav, setConfirmNav] = useState('');
  // Form state — cancel
  const [cancelReason, setCancelReason] = useState('');
  // Form state — settle
  const [settlementDate, setSettlementDate] = useState('');

  // Submission flags
  const [submitting, setSubmitting] = useState(false);

  // --- Data Fetching ---

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        size: rowsPerPage,
      };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterSearch) params.search = filterSearch;

      const data = await apiGet<{ items: any[]; total: number }>('/transactions', { params });
      const mapped: Transaction[] = (data.items || []).map((item: any) => ({
        ...item,
        reference: item.reference || item.transaction_ref || '',
        type: item.type || item.transaction_type || 'subscription',
        investor_name: item.investor_name || item.investor?.legal_name || '',
        share_class_name: item.share_class_name || item.share_class?.name || '',
      }));
      setTransactions(mapped);
      setTotalCount(data.total);
      LoggerService.info('[TransactionsPage] Loaded transactions', { total: data.total, page: page + 1 });
    } catch (err: any) {
      const msg = err?.message || 'Failed to load transactions';
      setError(msg);
      LoggerService.error('[TransactionsPage] Failed to load transactions', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPipeline = async () => {
    setPipelineLoading(true);
    setPipelineError(null);
    try {
      const data = await apiGet<SettlementPipelineItem[]>('/transactions/settlement-pipeline');
      setPipeline(data);
      LoggerService.info('[TransactionsPage] Loaded settlement pipeline', { count: data.length });
    } catch (err: any) {
      const msg = err?.message || 'Failed to load settlement pipeline';
      setPipelineError(msg);
      LoggerService.error('[TransactionsPage] Failed to load pipeline', err);
    } finally {
      setPipelineLoading(false);
    }
  };

  const fetchShareClasses = async () => {
    try {
      const data = await apiGet<ShareClass[]>('/transactions/share-classes');
      setShareClasses(data);
    } catch (err: any) {
      LoggerService.error('[TransactionsPage] Failed to load share classes', err);
    }
  };

  useEffect(() => {
    fetchShareClasses();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      fetchTransactions();
    } else {
      fetchPipeline();
    }
  }, [activeTab, page, rowsPerPage, filterStatus, filterType, filterDateFrom, filterDateTo, filterSearch]);

  // --- Actions ---

  const handleCreateSubscription = async () => {
    if (!subForm.investor_id || !subForm.share_class_id || !subForm.amount) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/transactions/subscriptions', {
        investor_id: subForm.investor_id,
        share_class_id: subForm.share_class_id,
        amount: parseFloat(subForm.amount),
        currency: subForm.currency || 'EUR',
      });
      enqueueSnackbar('Subscription order created', { variant: 'success' });
      setCreateSubOpen(false);
      setSubForm({ investor_id: '', share_class_id: '', amount: '', currency: '' });
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to create subscription', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Create subscription failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRedemption = async () => {
    if (!redForm.investor_id || !redForm.share_class_id || !redForm.units) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/transactions/redemptions', {
        investor_id: redForm.investor_id,
        share_class_id: redForm.share_class_id,
        units: parseFloat(redForm.units),
      });
      enqueueSnackbar('Redemption order created', { variant: 'success' });
      setCreateRedOpen(false);
      setRedForm({ investor_id: '', share_class_id: '', units: '' });
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to create redemption', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Create redemption failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSwitch = async () => {
    if (!switchForm.investor_id || !switchForm.from_share_class_id || !switchForm.to_share_class_id || !switchForm.units) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }
    if (switchForm.from_share_class_id === switchForm.to_share_class_id) {
      enqueueSnackbar('Source and target share classes must differ', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/transactions/switches', {
        investor_id: switchForm.investor_id,
        from_share_class_id: switchForm.from_share_class_id,
        to_share_class_id: switchForm.to_share_class_id,
        units: parseFloat(switchForm.units),
      });
      enqueueSnackbar('Switch order created', { variant: 'success' });
      setCreateSwitchOpen(false);
      setSwitchForm({ investor_id: '', from_share_class_id: '', to_share_class_id: '', units: '' });
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to create switch', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Create switch failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidate = async (tx: Transaction) => {
    setSubmitting(true);
    try {
      await apiPost(`/transactions/${tx.id}/validate`);
      enqueueSnackbar(`Transaction ${tx.reference} validated`, { variant: 'success' });
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Validation failed', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Validate failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedTx || !confirmNav) {
      enqueueSnackbar('NAV per share is required', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`/transactions/${selectedTx.id}/confirm`, {
        nav_per_share: parseFloat(confirmNav),
      });
      enqueueSnackbar(`Transaction ${selectedTx.reference} confirmed`, { variant: 'success' });
      setConfirmOpen(false);
      setConfirmNav('');
      setSelectedTx(null);
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Confirmation failed', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Confirm failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSettle = async () => {
    if (!selectedTx || !settlementDate) {
      enqueueSnackbar('Settlement date is required', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`/transactions/${selectedTx.id}/settle`, {
        settlement_date: settlementDate,
      });
      enqueueSnackbar(`Transaction ${selectedTx.reference} settled`, { variant: 'success' });
      setSettleOpen(false);
      setSettlementDate('');
      setSelectedTx(null);
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Settlement failed', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Settle failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedTx || !cancelReason.trim()) {
      enqueueSnackbar('Cancellation reason is required', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`/transactions/${selectedTx.id}/cancel`, {
        reason: cancelReason.trim(),
      });
      enqueueSnackbar(`Transaction ${selectedTx.reference} cancelled`, { variant: 'success' });
      setCancelOpen(false);
      setCancelReason('');
      setSelectedTx(null);
      fetchTransactions();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Cancellation failed', { variant: 'error' });
      LoggerService.error('[TransactionsPage] Cancel failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Helpers ---

  const formatCurrency = (value: number | null, currency: string): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'EUR' }).format(value);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getActiveStep = (status: string): number => {
    const idx = STATUS_STEPS.indexOf(status);
    return idx >= 0 ? idx : -1;
  };

  const handleShareClassChange = (scId: string, formType: 'sub' | 'red') => {
    const sc = shareClasses.find(s => s.id === scId);
    if (formType === 'sub') {
      setSubForm(prev => ({ ...prev, share_class_id: scId, currency: sc?.currency || '' }));
    } else {
      setRedForm(prev => ({ ...prev, share_class_id: scId }));
    }
  };

  // --- Render: Filter Bar ---

  const renderFilterBar = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="validated">Validated</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="settling">Settling</MenuItem>
              <MenuItem value="settled">Settled</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="subscription">Subscription</MenuItem>
              <MenuItem value="redemption">Redemption</MenuItem>
              <MenuItem value="switch_out">Switch Out</MenuItem>
              <MenuItem value="switch_in">Switch In</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Date From"
            type="date"
            value={filterDateFrom}
            onChange={(e) => { setFilterDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Date To"
            type="date"
            value={filterDateTo}
            onChange={(e) => { setFilterDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Ref, investor..."
            value={filterSearch}
            onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<TrendingUp />}
              onClick={() => setCreateSubOpen(true)}
              size="small"
            >
              Sub
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<TrendingDown />}
              onClick={() => setCreateRedOpen(true)}
              size="small"
            >
              Red
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<SwapHoriz />}
              onClick={() => setCreateSwitchOpen(true)}
              size="small"
            >
              Switch
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  // --- Render: Transaction Table ---

  const renderTransactionTable = () => (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ref</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Investor</TableCell>
              <TableCell>Share Class</TableCell>
              <TableCell align="right">Amount / Units</TableCell>
              <TableCell align="right">NAV</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {transactions.map((tx) => (
              <TableRow key={tx.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{tx.reference}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={tx.type.replace('_', ' ')}
                    color={TYPE_CHIP_COLOR[tx.type] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{tx.investor_name}</TableCell>
                <TableCell>{tx.share_class_name}</TableCell>
                <TableCell align="right">
                  {tx.amount !== null
                    ? formatCurrency(tx.amount, tx.currency)
                    : tx.units !== null
                      ? `${tx.units.toLocaleString()} units`
                      : '-'}
                </TableCell>
                <TableCell align="right">
                  {tx.nav_per_share !== null ? formatCurrency(tx.nav_per_share, tx.currency) : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={tx.status}
                    color={STATUS_CHIP_PROPS[tx.status]?.color || 'default'}
                    variant={STATUS_CHIP_PROPS[tx.status]?.variant || 'filled'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(tx.order_date)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      title="View details"
                      onClick={() => { setSelectedTx(tx); setDetailOpen(true); }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    {tx.status === 'submitted' && (
                      <IconButton
                        size="small"
                        title="Validate"
                        color="primary"
                        onClick={() => handleValidate(tx)}
                        disabled={submitting}
                      >
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    )}
                    {tx.status === 'validated' && (
                      <IconButton
                        size="small"
                        title="Confirm"
                        color="success"
                        onClick={() => { setSelectedTx(tx); setConfirmNav(''); setConfirmOpen(true); }}
                      >
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    )}
                    {tx.status === 'confirmed' && (
                      <IconButton
                        size="small"
                        title="Settle"
                        color="warning"
                        onClick={() => { setSelectedTx(tx); setSettlementDate(''); setSettleOpen(true); }}
                      >
                        <LocalShipping fontSize="small" />
                      </IconButton>
                    )}
                    {['draft', 'submitted', 'validated'].includes(tx.status) && (
                      <IconButton
                        size="small"
                        title="Cancel"
                        color="error"
                        onClick={() => { setSelectedTx(tx); setCancelReason(''); setCancelOpen(true); }}
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  // --- Render: Settlement Pipeline ---

  const renderPipeline = () => {
    const pipelineMap = new Map(pipeline.map(p => [p.status, p]));

    return (
      <Box>
        {pipelineLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {pipelineError && <Alert severity="error" sx={{ mb: 2 }}>{pipelineError}</Alert>}
        {!pipelineLoading && !pipelineError && (
          <Grid container spacing={2}>
            {PIPELINE_ORDER.map((status) => {
              const item = pipelineMap.get(status);
              const count = item?.count || 0;
              const total = item?.total_amount || 0;
              const currency = item?.currency || 'EUR';
              return (
                <Grid item xs={12} sm={6} md key={status}>
                  <Card
                    variant="outlined"
                    sx={{
                      textAlign: 'center',
                      borderColor: STATUS_CHIP_PROPS[status]?.color === 'success' ? 'success.main'
                        : STATUS_CHIP_PROPS[status]?.color === 'warning' ? 'warning.main'
                        : STATUS_CHIP_PROPS[status]?.color === 'primary' ? 'primary.main'
                        : STATUS_CHIP_PROPS[status]?.color === 'info' ? 'info.main'
                        : 'divider',
                      borderWidth: 2,
                    }}
                  >
                    <CardContent>
                      <Typography variant="overline" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        {status}
                      </Typography>
                      <Typography variant="h4" fontWeight={700}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(total, currency)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    );
  };

  // --- Render: Share class select ---

  const renderShareClassSelect = (
    value: string,
    onChange: (val: string) => void,
    label: string = 'Share Class'
  ) => (
    <FormControl fullWidth margin="normal">
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => onChange(e.target.value)}>
        {shareClasses.map((sc) => (
          <MenuItem key={sc.id} value={sc.id}>
            {sc.fund_name} - {sc.name} ({sc.currency})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // --- Render: Dialogs ---

  const renderCreateSubscriptionDialog = () => (
    <Dialog open={createSubOpen} onClose={() => setCreateSubOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>New Subscription Order</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Investor ID"
          value={subForm.investor_id}
          onChange={(e) => setSubForm(prev => ({ ...prev, investor_id: e.target.value }))}
        />
        {renderShareClassSelect(
          subForm.share_class_id,
          (val) => handleShareClassChange(val, 'sub')
        )}
        <TextField
          fullWidth
          margin="normal"
          label="Amount"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={subForm.amount}
          onChange={(e) => setSubForm(prev => ({ ...prev, amount: e.target.value }))}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Currency"
          value={subForm.currency}
          onChange={(e) => setSubForm(prev => ({ ...prev, currency: e.target.value }))}
          helperText="Auto-filled from share class selection"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateSubOpen(false)} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleCreateSubscription}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <Add />}
        >
          Create Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCreateRedemptionDialog = () => (
    <Dialog open={createRedOpen} onClose={() => setCreateRedOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>New Redemption Order</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Investor ID"
          value={redForm.investor_id}
          onChange={(e) => setRedForm(prev => ({ ...prev, investor_id: e.target.value }))}
        />
        {renderShareClassSelect(
          redForm.share_class_id,
          (val) => handleShareClassChange(val, 'red')
        )}
        <TextField
          fullWidth
          margin="normal"
          label="Units"
          type="number"
          inputProps={{ min: 0, step: 0.0001 }}
          value={redForm.units}
          onChange={(e) => setRedForm(prev => ({ ...prev, units: e.target.value }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateRedOpen(false)} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleCreateRedemption}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <Add />}
        >
          Create Redemption
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCreateSwitchDialog = () => (
    <Dialog open={createSwitchOpen} onClose={() => setCreateSwitchOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>New Switch Order</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Investor ID"
          value={switchForm.investor_id}
          onChange={(e) => setSwitchForm(prev => ({ ...prev, investor_id: e.target.value }))}
        />
        {renderShareClassSelect(
          switchForm.from_share_class_id,
          (val) => setSwitchForm(prev => ({ ...prev, from_share_class_id: val })),
          'From Share Class'
        )}
        {renderShareClassSelect(
          switchForm.to_share_class_id,
          (val) => setSwitchForm(prev => ({ ...prev, to_share_class_id: val })),
          'To Share Class'
        )}
        <TextField
          fullWidth
          margin="normal"
          label="Units"
          type="number"
          inputProps={{ min: 0, step: 0.0001 }}
          value={switchForm.units}
          onChange={(e) => setSwitchForm(prev => ({ ...prev, units: e.target.value }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateSwitchOpen(false)} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleCreateSwitch}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <SwapHoriz />}
        >
          Create Switch
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDetailDialog = () => {
    if (!selectedTx) return null;
    const step = getActiveStep(selectedTx.status);
    const isCancelled = selectedTx.status === 'cancelled' || selectedTx.status === 'rejected';

    return (
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedTx(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          Transaction Details - {selectedTx.reference}
        </DialogTitle>
        <DialogContent>
          {!isCancelled && (
            <Stepper activeStep={step} alternativeLabel sx={{ my: 3 }}>
              {STATUS_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label.charAt(0).toUpperCase() + label.slice(1)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          {isCancelled && (
            <Alert severity="error" sx={{ my: 2 }}>
              This transaction has been {selectedTx.status}.
              {selectedTx.cancel_reason && ` Reason: ${selectedTx.cancel_reason}`}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Reference</Typography>
              <Typography variant="body1">{selectedTx.reference}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="body1">
                <Chip label={selectedTx.type.replace('_', ' ')} color={TYPE_CHIP_COLOR[selectedTx.type]} size="small" />
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Investor</Typography>
              <Typography variant="body1">{selectedTx.investor_name} ({selectedTx.investor_id})</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Share Class</Typography>
              <Typography variant="body1">{selectedTx.share_class_name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Amount</Typography>
              <Typography variant="body1">{formatCurrency(selectedTx.amount, selectedTx.currency)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Units</Typography>
              <Typography variant="body1">{selectedTx.units !== null ? selectedTx.units.toLocaleString() : '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">NAV per Share</Typography>
              <Typography variant="body1">{formatCurrency(selectedTx.nav_per_share, selectedTx.currency)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Currency</Typography>
              <Typography variant="body1">{selectedTx.currency}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Order Date</Typography>
              <Typography variant="body1">{formatDate(selectedTx.order_date)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Settlement Date</Typography>
              <Typography variant="body1">{formatDate(selectedTx.settlement_date)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Typography variant="body1">
                <Chip
                  label={selectedTx.status}
                  color={STATUS_CHIP_PROPS[selectedTx.status]?.color || 'default'}
                  variant={STATUS_CHIP_PROPS[selectedTx.status]?.variant || 'filled'}
                  size="small"
                />
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">{formatDate(selectedTx.updated_at)}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {selectedTx.status === 'submitted' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => { setDetailOpen(false); handleValidate(selectedTx); }}
              disabled={submitting}
            >
              Validate
            </Button>
          )}
          {selectedTx.status === 'validated' && (
            <Button
              variant="contained"
              color="success"
              onClick={() => { setDetailOpen(false); setConfirmNav(''); setConfirmOpen(true); }}
              disabled={submitting}
            >
              Confirm
            </Button>
          )}
          {selectedTx.status === 'confirmed' && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => { setDetailOpen(false); setSettlementDate(''); setSettleOpen(true); }}
              disabled={submitting}
            >
              Settle
            </Button>
          )}
          {['draft', 'submitted', 'validated'].includes(selectedTx.status) && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => { setDetailOpen(false); setCancelReason(''); setCancelOpen(true); }}
              disabled={submitting}
            >
              Cancel Order
            </Button>
          )}
          <Button onClick={() => { setDetailOpen(false); setSelectedTx(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderConfirmDialog = () => {
    if (!selectedTx) return null;
    return (
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Transaction</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Confirming transaction <strong>{selectedTx.reference}</strong> for investor{' '}
            <strong>{selectedTx.investor_name}</strong>.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography>{selectedTx.type.replace('_', ' ')}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Share Class</Typography>
              <Typography>{selectedTx.share_class_name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Amount</Typography>
              <Typography>{formatCurrency(selectedTx.amount, selectedTx.currency)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Units</Typography>
              <Typography>{selectedTx.units !== null ? selectedTx.units.toLocaleString() : '-'}</Typography>
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="NAV per Share"
            type="number"
            inputProps={{ min: 0, step: 0.0001 }}
            value={confirmNav}
            onChange={(e) => setConfirmNav(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={submitting || !confirmNav}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderSettleDialog = () => {
    if (!selectedTx) return null;
    return (
      <Dialog open={settleOpen} onClose={() => setSettleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Settle Transaction</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Settling transaction <strong>{selectedTx.reference}</strong>.
          </Alert>
          <Stepper activeStep={1} sx={{ my: 3 }}>
            <Step completed>
              <StepLabel>Confirmed</StepLabel>
            </Step>
            <Step active>
              <StepLabel>Set Settlement Date</StepLabel>
            </Step>
            <Step>
              <StepLabel>Settled</StepLabel>
            </Step>
          </Stepper>
          <TextField
            fullWidth
            label="Settlement Date"
            type="date"
            value={settlementDate}
            onChange={(e) => setSettlementDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSettle}
            disabled={submitting || !settlementDate}
            startIcon={submitting ? <CircularProgress size={16} /> : <LocalShipping />}
          >
            Settle
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderCancelDialog = () => {
    if (!selectedTx) return null;
    return (
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Transaction</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to cancel transaction <strong>{selectedTx.reference}</strong>. This action cannot be undone.
          </Alert>
          <TextField
            fullWidth
            label="Cancellation Reason"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            required
            placeholder="Provide a reason for cancellation"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={submitting}>Go Back</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={submitting || !cancelReason.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : <Cancel />}
          >
            Cancel Transaction
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // --- Main Render ---

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Transactions
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Transaction List" />
          <Tab label="Settlement Pipeline" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          {renderFilterBar()}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!loading && !error && renderTransactionTable()}
        </>
      )}

      {activeTab === 1 && renderPipeline()}

      {renderCreateSubscriptionDialog()}
      {renderCreateRedemptionDialog()}
      {renderCreateSwitchDialog()}
      {renderDetailDialog()}
      {renderConfirmDialog()}
      {renderSettleDialog()}
      {renderCancelDialog()}
    </Box>
  );
};

export default TransactionsPage;
